/** @graphql cc **/
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { useCallback, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import throttle from 'lodash/throttle';
import gql from 'graphql-tag';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { ReplaceStep } from '@atlaskit/editor-prosemirror/transform';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { fireOfflineStorageAnalyticsEvent, parseErrorMessage } from '../../analytics';
import type { StepsDatabase } from '../../stepsDatabase';
import {
	showVersionInsertionErrorFlag,
	showVersionInsertionSuccessFlag,
	hideVersionInsertionErrorFlag,
} from '../versionInsertionFlags';

import type {
	EditorOfflineStorageInsertVersionMutation,
	EditorOfflineStorageInsertVersionMutationVariables,
} from './__types__/EditorOfflineStorageInsertVersionMutation';

const i18n = defineMessages({
	VersionComment: {
		id: 'editor-offline-storage.offline-version-comment',
		defaultMessage: 'Offline edits',
		description: 'Description of document version that contains offline edits',
	},
	VersionCommentManualRetry: {
		id: 'editor-offline-storage.offline-version-comment-manual-retry',
		defaultMessage: 'Offline edits (manual retry)',
		description: 'Description of document version that contains offline edits from manual retry',
	},
});

// Tech-spec:
// https://hello.atlassian.net/wiki/spaces/~712020fa3d29df95e344e18f663fa84c3d65c8/pages/5567284738/Tech+Spec+Version+History+Creation+API+with+Offline+Copy
const EditorOfflineStorageInsertOfflineVersionMutation = gql`
	mutation useCreateOfflineVersionInsertVersionMutation(
		$cloudId: ID!
		$contentId: ID!
		$adfContent: String!
		$versionComment: String!
	) {
		confluence_insertOfflineVersion(
			cloudId: $cloudId
			input: { contentId: $contentId, adfContent: $adfContent, versionComment: $versionComment }
		) {
			versionNumber
			success
			errors {
				message
			}
		}
	}
`;

// Limit how often we can try to create new versions
// P50 disconnection time is ~7 seconds so this tries to minimise new versions
// if someone's connection is flaky.
const MAX_INTERVAL = 20000;

export const useCreateOfflineVersion = ({
	contentId,
	api,
	stepsDatabase,
	flags,
}: {
	contentId: string | undefined;
	api: PublicPluginAPI<[AnalyticsPlugin]> | undefined;
	stepsDatabase: StepsDatabase;
	flags: FlagsStateContainer | null;
}) => {
	const { cloudId } = useSessionData();
	const intl = useIntl();
	const manualRetryCountRef = useRef(0);

	const [createOfflineVersion] = useMutation<
		EditorOfflineStorageInsertVersionMutation,
		EditorOfflineStorageInsertVersionMutationVariables
	>(EditorOfflineStorageInsertOfflineVersionMutation);

	const createVersionUnthrottled = useCallback(
		({
			cloudId: paramCloudId,
			contentId: paramContentId,
			adfContent,
			versionComment,
		}: {
			cloudId: string;
			contentId: string;
			adfContent: string;
			versionComment: string;
		}) => {
			// Create onRetry callback that will be reused for both error paths
			const onRetry = () => {
				if (!flags) {
					return;
				}

				// Increment manual retry count when user clicks retry
				manualRetryCountRef.current += 1;

				hideVersionInsertionErrorFlag(flags);

				createVersionUnthrottled({
					cloudId: paramCloudId,
					contentId: paramContentId,
					adfContent,
					versionComment: intl.formatMessage(i18n.VersionCommentManualRetry),
				});
			};

			const onClose = () => {
				fireOfflineStorageAnalyticsEvent({
					api,
					action: 'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_DISMISSED',
					manualRetryCount: manualRetryCountRef.current,
				});
			};

			createOfflineVersion({
				variables: {
					cloudId: paramCloudId,
					contentId: paramContentId,
					adfContent,
					versionComment,
				},
			})
				.then(async (res) => {
					if (res.errors) {
						fireOfflineStorageAnalyticsEvent({
							api,
							action: 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION',
							errorMessage: res.errors.map((err) => err.message).join(','),
							manualRetryCount: manualRetryCountRef.current,
						});

						// Store the backup and show flag for later retry
						if (fg('platform_editor_offline_editing_web_ga_fixes')) {
							await stepsDatabase.storeVersionInsertionBackup(adfContent);

							if (flags && paramContentId) {
								showVersionInsertionErrorFlag(
									flags,
									intl,
									onRetry,
									onClose,
									manualRetryCountRef.current,
								);
							}
						}
						return;
					}

					fireOfflineStorageAnalyticsEvent({
						api,
						action: 'OFFLINE_EDITING_SUCCESS_STORE_RECOVERY_VERSION',
						success: res.data?.confluence_insertOfflineVersion?.success,
						manualRetryCount: manualRetryCountRef.current,
					});

					// Show success flag only after manual retries
					if (manualRetryCountRef.current > 0 && flags) {
						showVersionInsertionSuccessFlag(flags, intl);
					}

					// Reset manual retry count on success
					manualRetryCountRef.current = 0;

					// Clear the stored backup on any successful version insertion
					if (fg('platform_editor_offline_editing_web_ga_fixes')) {
						await stepsDatabase.clearVersionInsertionBackup();
					}
				})
				.catch(async (e) => {
					fireOfflineStorageAnalyticsEvent({
						api,
						action: 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION',
						errorMessage: parseErrorMessage(e),
						manualRetryCount: manualRetryCountRef.current,
					});

					// Store the backup and show flag for later retry
					if (fg('platform_editor_offline_editing_web_ga_fixes')) {
						await stepsDatabase.storeVersionInsertionBackup(adfContent);

						if (flags && paramContentId) {
							showVersionInsertionErrorFlag(
								flags,
								intl,
								onRetry,
								onClose,
								manualRetryCountRef.current,
							);
						}
					}
				});
		},
		[createOfflineVersion, api, stepsDatabase, flags, intl],
	);

	/**
	 * Manually retry version insertion with stored backup content
	 * Hides the flag and calls the core createVersionUnthrottled function
	 */
	const manualRetryCreateVersion = useCallback(
		(storedAdfContent: string) => {
			if (!contentId || !flags) {
				return;
			}

			hideVersionInsertionErrorFlag(flags);
			createVersionUnthrottled({
				cloudId,
				contentId,
				adfContent: storedAdfContent,
				versionComment: intl.formatMessage(i18n.VersionCommentManualRetry),
			});
		},
		[contentId, cloudId, flags, intl, createVersionUnthrottled],
	);

	const throttledCreateVersion = useMemo(
		() => throttle(createVersionUnthrottled, MAX_INTERVAL, { leading: true, trailing: false }),
		[createVersionUnthrottled],
	);

	return {
		createVersion: useCallback(
			({ doc, steps }: { doc: PMNode; steps: ProseMirrorStep[] }) => {
				// Only create a new version if there are document changes
				// To avoid noisy versions we filter out any non-ReplaceSteps
				// Ignore ReplaceAroundSteps and other steps that aren't as likely to be intended by the user
				// Note: This is a best-effort approach and may not cover all cases
				if (steps.filter((s) => s instanceof ReplaceStep).length === 0) {
					return;
				}
				if (!contentId) {
					fireOfflineStorageAnalyticsEvent({
						api,
						action: 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION',
						errorMessage: 'No content id',
						manualRetryCount: 0,
					});
					return;
				}
				const adfContent = new JSONTransformer().encode(doc);

				throttledCreateVersion({
					cloudId,
					contentId,
					adfContent: JSON.stringify(adfContent),
					versionComment: intl.formatMessage(i18n.VersionComment),
				});
			},
			[api, contentId, throttledCreateVersion, cloudId, intl],
		),
		manualRetryCreateVersion,
	};
};
